/**
 * Logique pure du blog (aucune dépendance à React) : temps de lecture réel, sommaire, articles liés,
 * métadonnées et données structurées. Utilisée telle quelle par les pages React et par le pré-rendu
 * du build, ce qui garantit que le HTML servi aux robots et le DOM rendu par React disent la même chose.
 *
 * Format d'un article : voir l'en-tête de `src/data/blogPosts.js`.
 */
import { inlineToText } from "./inline.js";
import {
  BLOG_NAME,
  LOGO_URL,
  OG_IMAGE_URL,
  SITE_LANG,
  SITE_NAME,
  SITE_URL,
  absoluteUrl,
  postPath,
  postUrl,
} from "./site.js";

const WORDS_PER_MINUTE = 200;

const fold = (value) =>
  String(value)
    .normalize("NFD")
    .replace(/[̀-ͯ]/g, "")
    .toLowerCase();

/* ------------------------------------------------------------------ texte & mesures */

/** Texte brut d'un bloc de contenu. */
export function blockText(block) {
  if (block.h2) return block.h2;
  if (block.h3) return block.h3;
  if (block.p) return inlineToText(block.p);
  if (block.note) return inlineToText(block.note);
  if (block.code) return block.code;
  if (block.ul) return block.ul.map(inlineToText).join(" ");
  if (block.ol) return block.ol.map(inlineToText).join(" ");
  if (block.table) {
    return [...(block.table.head || []), ...(block.table.rows || []).flat()].map(inlineToText).join(" ");
  }
  return "";
}

/** Tout le texte lisible d'un article (résumé, corps, FAQ). */
export function postText(post) {
  const parts = [];
  if (post.summary) parts.push(inlineToText(post.summary));
  (post.body || []).forEach((block) => parts.push(blockText(block)));
  (post.faq || []).forEach((item) => parts.push(item.q, inlineToText(item.a)));
  return parts.filter(Boolean).join(" ");
}

export function wordCount(post) {
  return postText(post).split(/\s+/).filter(Boolean).length;
}

/** Temps de lecture calculé sur le contenu réel (l'ancien champ `readingTime` saisi à la main était faux). */
export function readingTimeMin(post) {
  return Math.max(1, Math.round(wordCount(post) / WORDS_PER_MINUTE));
}

/** Plus récent d'abord ; à date égale, l'ordre du tableau source fait foi. */
export function sortNewestFirst(posts) {
  return posts
    .map((post, index) => ({ post, index }))
    .sort((a, b) => {
      if (a.post.date !== b.post.date) return a.post.date < b.post.date ? 1 : -1;
      return a.index - b.index;
    })
    .map(({ post }) => post);
}

/* ------------------------------------------------------------------ sommaire */

export function slugifyHeading(text) {
  return fold(text)
    .replace(/[^a-z0-9]+/g, "-")
    .slice(0, 60)
    .replace(/^-+|-+$/g, "");
}

/**
 * Titres h2/h3 avec un identifiant d'ancre unique, dans l'ordre du corps.
 * `index` est la position du bloc dans `post.body` (les rendus s'en servent pour retrouver leur id).
 */
export function getHeadings(post) {
  const seen = new Map();
  const headings = [];
  (post.body || []).forEach((block, index) => {
    const level = block.h2 ? 2 : block.h3 ? 3 : 0;
    if (!level) return;
    const text = block.h2 || block.h3;
    const base = slugifyHeading(text) || `section-${index}`;
    const n = seen.get(base) || 0;
    seen.set(base, n + 1);
    headings.push({ index, level, text, id: n ? `${base}-${n + 1}` : base });
  });
  return headings;
}

/** Un sommaire n'aide que sur les articles assez longs. */
export function hasToc(post) {
  return getHeadings(post).filter((h) => h.level === 2).length >= 4;
}

/* ------------------------------------------------------------------ maillage interne */

const keywordTokens = (keywords) =>
  new Set(
    (keywords || [])
      .flatMap((k) => fold(k).split(/[^a-z0-9]+/))
      .filter((w) => w.length >= 4),
  );

/**
 * Articles à lire ensuite : d'abord ceux choisis à la main (`post.related`), puis les plus proches par
 * mots-clés communs, catégorie et statut de guide de référence (`pillar`) — ce qui fait remonter le
 * jus de lien des petits articles vers les guides qui doivent ranker.
 */
export function getRelatedPosts(post, all, limit = 3) {
  const curated = (post.related || []).map((slug) => all.find((p) => p.slug === slug)).filter(Boolean);
  const taken = new Set([post.slug, ...curated.map((p) => p.slug)]);
  const ownKeywords = new Set((post.keywords || []).map(fold));
  const ownTokens = keywordTokens(post.keywords);

  const ranked = all
    .map((candidate, index) => ({ candidate, index }))
    .filter(({ candidate }) => !taken.has(candidate.slug))
    .map(({ candidate, index }) => {
      let score = 0;
      (candidate.keywords || []).forEach((k) => {
        if (ownKeywords.has(fold(k))) score += 4;
      });
      keywordTokens(candidate.keywords).forEach((w) => {
        if (ownTokens.has(w)) score += 1;
      });
      if (candidate.category === post.category) score += 2;
      if (candidate.pillar) score += 3;
      return { candidate, index, score };
    })
    .filter(({ score }) => score > 0)
    .sort((a, b) => {
      if (b.score !== a.score) return b.score - a.score;
      if (a.candidate.date !== b.candidate.date) return a.candidate.date < b.candidate.date ? 1 : -1;
      return a.index - b.index;
    });

  return [...curated, ...ranked.map(({ candidate }) => candidate)].slice(0, limit);
}

/* ------------------------------------------------------------------ métadonnées */

export function postMeta(post) {
  return {
    title: `${post.title} — ${SITE_NAME}`,
    description: post.description,
    canonical: postUrl(post.slug),
  };
}

export function blogIndexMeta(posts) {
  return {
    title: `${BLOG_NAME} — Gestion de projet & diagrammes de Gantt`,
    description: `${posts.length} guides et articles pratiques sur la gestion de projet, les diagrammes de Gantt, la planification, les dépendances FS/SS/FF/SF, les modèles de planning et la productivité.`,
    canonical: absoluteUrl("/blog"),
  };
}

export function postBreadcrumb(post) {
  return [
    { name: "Accueil", path: "/" },
    { name: "Blog", path: "/blog" },
    { name: post.title, path: postPath(post.slug) },
  ];
}

/* ------------------------------------------------------------------ données structurées (JSON-LD) */

const ORGANIZATION = { "@type": "Organization", name: SITE_NAME, url: SITE_URL };
const PUBLISHER = { ...ORGANIZATION, logo: { "@type": "ImageObject", url: LOGO_URL } };

export function buildBlogPostingJsonLd(post) {
  const url = postUrl(post.slug);
  return {
    "@context": "https://schema.org",
    "@type": "BlogPosting",
    mainEntityOfPage: { "@type": "WebPage", "@id": url },
    headline: post.title,
    description: post.description,
    url,
    image: [OG_IMAGE_URL],
    datePublished: post.date,
    dateModified: post.updated || post.date,
    inLanguage: SITE_LANG,
    author: ORGANIZATION,
    publisher: PUBLISHER,
    keywords: (post.keywords || []).join(", "),
    articleSection: post.category,
    wordCount: wordCount(post),
    isPartOf: { "@type": "Blog", name: BLOG_NAME, url: absoluteUrl("/blog") },
  };
}

/** `items` : `[{ q, a }]` — `a` accepte la mini-syntaxe d'`inline.js`. */
export function buildFaqJsonLd(items) {
  return {
    "@context": "https://schema.org",
    "@type": "FAQPage",
    mainEntity: items.map(({ q, a }) => ({
      "@type": "Question",
      name: q,
      acceptedAnswer: { "@type": "Answer", text: inlineToText(a) },
    })),
  };
}

/** `trail` : `[{ name, path }]` du plus général au plus précis. */
export function buildBreadcrumbJsonLd(trail) {
  return {
    "@context": "https://schema.org",
    "@type": "BreadcrumbList",
    itemListElement: trail.map((item, i) => ({
      "@type": "ListItem",
      position: i + 1,
      name: item.name,
      item: absoluteUrl(item.path),
    })),
  };
}

export function buildBlogIndexJsonLd(posts) {
  return {
    "@context": "https://schema.org",
    "@type": "Blog",
    name: BLOG_NAME,
    url: absoluteUrl("/blog"),
    description: "Ressources gestion de projet et planification Gantt.",
    inLanguage: SITE_LANG,
    publisher: PUBLISHER,
    blogPost: sortNewestFirst(posts).map((p) => ({
      "@type": "BlogPosting",
      headline: p.title,
      url: postUrl(p.slug),
      datePublished: p.date,
      dateModified: p.updated || p.date,
      description: p.description,
      keywords: (p.keywords || []).join(", "),
    })),
  };
}
